import React from "react";
import SectionLabel from "./brand/SectionLabel";

/**
 * Reusable Manufacturer Certifications block.
 *
 * Two variants:
 *   - "full"     Full section with overline, heading and three-column grid.
 *                Used on the homepage between About and Careers, and near the
 *                top of the /cell-boosters and /cellular-das service pages.
 *   - "compact"  Slim strip without the outer heading — used near the bottom
 *                of local hub pages (/lethbridge, /southern-alberta).
 *
 * Intentionally text-only for now; official manufacturer badge artwork can be
 * slotted in later as image children without restructuring the layout.
 */

const CREDENTIALS = [
  {
    id: "surecall",
    manufacturer: "SureCall",
    lines: ["Certified Installer"],
    testId: "cert-surecall",
  },
  {
    id: "wilsonpro",
    manufacturer: "WilsonPro",
    lines: ["Certified Installer / Integrator"],
    testId: "cert-wilsonpro",
  },
  {
    id: "nextivity",
    manufacturer: "Nextivity",
    lines: [
      "Certified Partner — Professional Level",
      "CEL-FI Enterprise Sales & Installation Certified",
    ],
    testId: "cert-nextivity",
  },
];

function CredentialTile({ manufacturer, lines, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-bg-secondary border border-steel-dark p-8 md:p-10 hover:bg-[#16181B] transition-colors"
    >
      <div className="font-mono text-[10px] tracking-[0.25em] text-signal uppercase mb-4">
        Certification
      </div>
      <div className="heading text-2xl md:text-3xl text-white uppercase leading-tight mb-3">
        {manufacturer}
      </div>
      <div className="space-y-1">
        {lines.map((line) => (
          <div
            key={line}
            className="text-steel-light text-sm md:text-base leading-relaxed"
          >
            {line}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Certifications({ variant = "full", className = "" }) {
  const grid = (
    <div
      data-testid="certifications-grid"
      className="grid grid-cols-1 md:grid-cols-3 gap-[1px] bg-steel-dark border border-steel-dark"
    >
      {CREDENTIALS.map((c) => (
        <CredentialTile key={c.id} {...c} />
      ))}
    </div>
  );

  if (variant === "compact") {
    return (
      <section
        data-testid="certifications-compact"
        className={`relative py-10 md:py-14 bg-bg-primary ${className}`}
        aria-label="Manufacturer certifications"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionLabel className="mb-5">Manufacturer Certifications</SectionLabel>
          {grid}
        </div>
      </section>
    );
  }

  return (
    <section
      id="certifications"
      data-testid="certifications-section"
      className={`relative py-20 md:py-28 bg-bg-primary border-t border-steel-dark ${className}`}
      aria-label="Manufacturer certifications"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-12 gap-8 mb-12">
          <div className="md:col-span-4">
            <SectionLabel className="mb-5">Credentials</SectionLabel>
            <h2 className="heading text-4xl md:text-5xl lg:text-6xl text-white uppercase leading-[0.95]">
              Manufacturer <br />
              <span className="text-signal">Certifications.</span>
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 flex items-end">
            <p className="text-steel-light text-base md:text-lg leading-relaxed">
              Formal manufacturer training on the specific product lines we
              install. Certification is not authorization — we are not resellers
              or manufacturer representatives — but it does mean we've been
              through the vendor's own installation, tuning and validation
              training for the systems we deploy in the field.
            </p>
          </div>
        </div>
        {grid}
      </div>
    </section>
  );
}
